(function () {
  'use strict';

  const CFG      = window.__DUCATI_CONFIG__ || {};
  const S3       = CFG.s3Base || 'https://ducaticloseoutparts.s3.us-east-1.amazonaws.com';
  const SHOP     = CFG.shopDomain || (window.Shopify && window.Shopify.shop) || window.location.hostname;
  const FAMILIES = CFG.families || [];

  const CAT_KW = {
    'Exhaust':     ['silencer','exhaust','manifold','race-line','muffler'],
    'Lighting':    ['headlight','led','indicator','fairing','trim','light'],
    'Luggage':     ['pannier','bag','rack','top case','luggage','tank bag','handlebar bag'],
    'Seats':       ['seat'],
    'Protection':  ['guard','cover','protection','grille','protector','disc lock','anti-theft','plug','belly pan'],
    'Mirrors':     ['mirror','rear-view'],
    'Electronics': ['multimedia','quick shift','dms','dqs','smartphone','navigation','heated','tyre'],
    'Style':       ['sticker','fairings','stripe','canvas','paint','spoke','handlebar','tank cap',
                    'mudguard','balancing','oil filler','rim','number plate','bracket','adapters'],
  };
  const STATUS_LABEL = { A: 'Active', F: 'Final buy', X: 'Discontinued' };

  // State
  let selFamily   = null;
  let selModel    = '';
  let selSubModel = '';
  let selYear     = '';
  let activeItems = [];
  let activeCat   = 'All';
  let indexData   = null;

  // Caches
  const indexCache    = {};
  const detailCache   = {};
  const shopifyCache  = {}; // sku -> { found, variantId, price, available, productUrl, productTitle }
  const superModelMap = {}; // accFamilyId -> Set<superModel description (uppercase)>

  // ── DOM helpers ───────────────────────────────────────────────────────────

  const root = document.getElementById('ducati-accessories-root');
  function $(id) { return document.getElementById(id); }
  function escHtml(s) {
    return String(s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── Shopify SKU lookup ────────────────────────────────────────────────────
  // Uses Shopify's Ajax search API — no token required, works on any storefront.
  // /search/suggest.json?q={sku}&resources[type]=product matches variant SKUs.

  async function lookupSku(sku) {
    if (!sku) return null;
    if (shopifyCache[sku] !== undefined) return shopifyCache[sku];

    try {
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=5`;
      const res = await fetch(searchUrl);
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];

      let match = null;
      for (const p of products) {
        // Extract handle from the url field — suggest API returns
        // url like "/products/96180551aa" or "/products/96180551aa?_pos=1&..."
        const rawUrl  = p.url || p.handle || '';
        const handle  = rawUrl.split('/products/')[1]
          ? rawUrl.split('/products/')[1].split('?')[0].split('#')[0]
          : null;
        if (!handle) continue;

        const pRes = await fetch(`/products/${handle}.js`);
        if (!pRes.ok) continue;
        const pData = await pRes.json();

        const skuUpper = sku.toUpperCase().trim();

        // Strategy 1: exact variant SKU field match (case-insensitive)
        let variant = (pData.variants || []).find(
          v => v.sku && v.sku.toUpperCase().trim() === skuUpper
        );

        // Strategy 2: variant SKU is null but handle ends with the SKU
        // e.g. "streetfighter-v4-race-exhaust-96482251aa" matches "96482251AA"
        if (!variant && pData.handle && pData.handle.toUpperCase().endsWith(skuUpper)) {
          variant = pData.variants && pData.variants[0];
        }

        // Strategy 3: SKU appears anywhere in the handle (looser fallback)
        if (!variant && pData.handle && pData.handle.toUpperCase().includes(skuUpper)) {
          variant = pData.variants && pData.variants[0];
        }

        if (variant) {
          match = {
            found:        true,
            variantId:    variant.id,
            price:        variant.price,
            compareAt:    variant.compare_at_price,
            available:    variant.available,
            productTitle: pData.title,
            productUrl:   `/products/${pData.handle}?variant=${variant.id}`,
            handle:       pData.handle,
          };
          break;
        }
      }

      // Strategy: if suggest returned no match, try fetching the SKU directly as a handle
      // Many Ducati products are published with the SKU as their handle (e.g. /products/96482251aa)
      if (!match) {
        try {
          const directRes = await fetch(`/products/${sku.toLowerCase()}.js`);
          if (directRes.ok) {
            const pData = await directRes.json();
            const skuUpper = sku.toUpperCase().trim();
            let variant = (pData.variants || []).find(
              v => v.sku && v.sku.toUpperCase().trim() === skuUpper
            );
            if (!variant) variant = pData.variants && pData.variants[0];
            if (variant) {
              match = {
                found:        true,
                variantId:    variant.id,
                price:        variant.price,
                compareAt:    variant.compare_at_price,
                available:    variant.available,
                productTitle: pData.title,
                productUrl:   `/products/${pData.handle}?variant=${variant.id}`,
                handle:       pData.handle,
              };
            }
          }
        } catch (e2) { /* not found via direct handle */ }
      }

      shopifyCache[sku] = match || { found: false };
      return shopifyCache[sku];
    } catch (e) {
      console.warn('Shopify SKU lookup failed for', sku, e);
      shopifyCache[sku] = { found: false };
      return shopifyCache[sku];
    }
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Add to cart via Shopify Ajax API
  async function addToCart(variantId, btnEl) {
    btnEl.disabled   = true;
    btnEl.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error(res.status);
      btnEl.textContent = 'Added!';
      btnEl.style.background = '#1D9E75';
      // Refresh cart count if theme exposes it
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(() => {
        btnEl.disabled   = false;
        btnEl.textContent = 'Add to cart';
        btnEl.style.background = '';
      }, 2000);
    } catch (e) {
      btnEl.disabled   = false;
      btnEl.textContent = 'Error — try again';
      setTimeout(() => { btnEl.textContent = 'Add to cart'; }, 2000);
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    root.innerHTML = `
      <div class="dac-eyebrow">Ducati Performance &middot; Accessories</div>
      <h1 class="dac-title">Find accessories for your bike</h1>
      <p class="dac-subtitle">Select your family and model to browse compatible accessories.</p>

      <div class="dac-card" id="dac-step1">
        <div class="dac-step-label">
          <span class="dac-step-num" id="dac-sn1">1</span>
          Choose a family
        </div>
        <div class="dac-family-grid" id="dac-family-grid">
          <div class="dac-loading" style="grid-column:1/-1">
            <span class="dac-spinner"></span>Loading families&hellip;
          </div>
        </div>
      </div>

      <div class="dac-card dac-hidden" id="dac-step2">
        <div class="dac-step-label">
          <span class="dac-step-num" id="dac-sn2">2</span>
          Choose model &amp; year
        </div>
        <div class="dac-model-row">
          <div class="dac-select-wrap">
            <div class="dac-select-label">Model</div>
            <select id="dac-model-select" disabled>
              <option value="">— Loading… —</option>
            </select>
          </div>
          <div class="dac-select-wrap">
            <div class="dac-select-label">Variant</div>
            <select id="dac-submodel-select" disabled>
              <option value="">— Select model first —</option>
            </select>
          </div>
          <div class="dac-select-wrap">
            <div class="dac-select-label">Year</div>
            <select id="dac-year-select" disabled>
              <option value="">— Select variant first —</option>
            </select>
          </div>
        </div>
        <div class="dac-cta-row">
          <button class="dac-btn" id="dac-view-btn" disabled>View accessories</button>
          <span class="dac-sel-summary" id="dac-sel-summary"></span>
        </div>
      </div>

      <div class="dac-hidden" id="dac-results">
        <hr class="dac-divider">
        <div class="dac-results-header">
          <div>
            <div class="dac-results-title" id="dac-results-title"></div>
            <div class="dac-results-meta"  id="dac-results-meta"></div>
          </div>
          <button class="dac-reset-btn" id="dac-reset-btn">Change selection</button>
        </div>
        <div class="dac-filter-bar">
          <input type="text" id="dac-search" placeholder="Search accessories&hellip;">
          <select id="dac-lc-filter">
            <option value="all">All items</option>
            <option value="0">Available</option>
            <option value="1">Last chance</option>
          </select>
        </div>
        <div class="dac-chip-bar" id="dac-chips"></div>
        <div class="dac-acc-grid"  id="dac-acc-grid"></div>
        <div class="dac-empty dac-hidden" id="dac-empty">No accessories match your filter.</div>
      </div>

      <div class="dac-overlay" id="dac-overlay">
        <div class="dac-modal" id="dac-modal">
          <button class="dac-modal-close" id="dac-modal-close">&#x2715;</button>
          <div id="dac-modal-content"></div>
        </div>
      </div>
    `;

    bindEvents();
    loadEpcData();
  }

  // ── EPC data loading ──────────────────────────────────────────────────────

  async function loadEpcData() {
    try {
      const res = await fetch(`${S3}/ducati_epc/manifest/families.json`);
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();

      const epcByDesc = {};
      (data.families || []).forEach(f => { epcByDesc[f.description.toUpperCase()] = f.id; });

      const epcIdByAcc = {};
      FAMILIES.forEach(f => {
        const epcId = epcByDesc[f.name.toUpperCase()];
        if (epcId) epcIdByAcc[f.id] = epcId;
      });

      await Promise.all(
        Object.entries(epcIdByAcc).map(async ([accId, epcId]) => {
          try {
            const r = await fetch(`${S3}/ducati_epc/manifest/supermodels/${epcId}.json`);
            if (!r.ok) throw new Error(r.status);
            const d = await r.json();
            superModelMap[accId] = new Set(
              (d.superModels || []).map(sm => sm.description.toUpperCase())
            );
          } catch (e) {
            superModelMap[accId] = new Set();
          }
        })
      );
    } catch (e) {
      console.warn('EPC family data unavailable:', e);
    }
    buildFamilyGrid();
  }

  // ── Family grid ───────────────────────────────────────────────────────────

  function buildFamilyGrid() {
    $('dac-family-grid').innerHTML = FAMILIES.map(f => {
      const imgUrl = f.imgKey
        ? `${S3}/ducati_epc/family_img/${f.imgKey}/${f.imgKey}_BIG.png`
        : null;
      return `
        <button class="dac-family-btn"
          data-id="${f.id}" data-name="${escHtml(f.name)}" data-index="${escHtml(f.indexFile)}">
          ${imgUrl
            ? `<img class="dac-family-img" src="${imgUrl}" alt="${escHtml(f.name)}"
                 onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">`
            : ''}
          <div class="dac-family-img-placeholder" style="${imgUrl ? 'display:none' : ''}">
            ${f.icon || '🏍'}
          </div>
          <span class="dac-family-name">${escHtml(f.name)}</span>
        </button>`;
    }).join('');
  }

  async function selectFamily(id, name, indexFile) {
    selFamily    = { id, name, indexFile };
    selModel     = ''; selSubModel = ''; selYear = '';
    indexData    = null;

    document.querySelectorAll('.dac-family-btn').forEach(b => b.classList.remove('selected'));
    document.querySelector(`.dac-family-btn[data-id="${id}"]`).classList.add('selected');
    $('dac-sn1').classList.add('done');
    $('dac-step2').classList.remove('dac-hidden');
    $('dac-results').classList.add('dac-hidden');
    $('dac-view-btn').disabled       = true;
    $('dac-sel-summary').textContent = '';
    $('dac-model-select').innerHTML    = '<option value="">Loading\u2026</option>';
    $('dac-model-select').disabled     = true;
    $('dac-submodel-select').innerHTML = '<option value="">— Select model first —</option>';
    $('dac-submodel-select').disabled  = true;
    $('dac-year-select').innerHTML     = '<option value="">— Select variant first —</option>';
    $('dac-year-select').disabled      = true;

    await loadIndexData(id, indexFile);
    buildModelSelect();
  }

  async function loadIndexData(id, indexFile) {
    if (indexCache[id]) { indexData = indexCache[id]; return; }
    try {
      const url = `${S3}/accessories/index/by_family/${encodeURIComponent(indexFile)}.json`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      indexCache[id] = await res.json();
      indexData = indexCache[id];
    } catch (e) {
      console.warn('Index data fetch failed:', indexFile, e.message);
      // Store error state so buildModelSelect can show a helpful message
      indexCache[id] = { superModels: {}, fetchError: e.message };
      indexData = indexCache[id];
    }
  }

  // ── Selects ───────────────────────────────────────────────────────────────

  function getValidSuperModels() {
    if (!indexData || !indexData.superModels) return [];
    const allKeys  = Object.keys(indexData.superModels);
    const familyCfg = FAMILIES.find(f => f.id === selFamily.id);

    // Priority 1: explicit validModels list in Liquid config
    // Use this to hardcode the correct superModel keys when EPC names don't match index keys
    if (familyCfg && familyCfg.validModels && familyCfg.validModels.length) {
      const validSet = new Set(familyCfg.validModels.map(m => m.toUpperCase()));
      const filtered = allKeys.filter(k => validSet.has(k.toUpperCase()));
      if (filtered.length) return filtered.sort();
    }

    // Priority 2: noFilter flag — show everything
    if (familyCfg && familyCfg.noFilter) return allKeys.sort();

    // Priority 3: EPC supermodel manifest
    const epcSet = superModelMap[selFamily.id];
    if (!epcSet || epcSet.size === 0) return allKeys.sort(); // no EPC data, show all

    const filtered = allKeys.filter(k => epcSet.has(k.toUpperCase()));

    // Priority 4: EPC filter eliminated everything — fall back to showing all
    if (filtered.length === 0) return allKeys.sort();

    return filtered.sort();
  }

  function buildModelSelect() {
    const ms = $('dac-model-select');

    // Show error if index file failed to load (e.g. S3 Access Denied)
    if (indexData && indexData.fetchError) {
      ms.innerHTML = '<option value="">Index unavailable — check S3 permissions</option>';
      ms.disabled  = true;
      return;
    }

    const models = getValidSuperModels();
    if (!models.length) {
      ms.innerHTML = '<option value="">No models available</option>';
      ms.disabled  = true;
      return;
    }
    ms.innerHTML = '<option value="">— Select model —</option>' +
      models.map(m => `<option value="${escHtml(m)}">${escHtml(m)}</option>`).join('');
    ms.disabled = false;
  }

  function onModelChange() {
    selModel = $('dac-model-select').value;
    selSubModel = ''; selYear = '';
    $('dac-year-select').innerHTML  = '<option value="">— Select variant first —</option>';
    $('dac-year-select').disabled   = true;
    $('dac-view-btn').disabled      = true;
    const ss = $('dac-submodel-select');
    if (!selModel || !indexData) { ss.innerHTML = '<option value="">— Select model first —</option>'; ss.disabled = true; return; }
    const subs = Object.keys(indexData.superModels[selModel] || {}).sort();
    if (!subs.length) { ss.innerHTML = '<option value="">No variants</option>'; ss.disabled = true; return; }
    ss.innerHTML = '<option value="">— Select variant —</option>' +
      subs.map(s => `<option value="${escHtml(s)}">${escHtml(s)}</option>`).join('');
    ss.disabled = false;
  }

  function onSubModelChange() {
    selSubModel = $('dac-submodel-select').value;
    selYear = '';
    $('dac-view-btn').disabled = true;
    const ys = $('dac-year-select');
    if (!selSubModel || !selModel || !indexData) { ys.innerHTML = '<option value="">— Select variant first —</option>'; ys.disabled = true; return; }
    const yearObj    = indexData.superModels[selModel][selSubModel] || {};
    const years      = Object.keys(yearObj).filter(y => y !== 'UNKNOWN_YEAR').sort((a, b) => b - a);
    const hasUnknown = 'UNKNOWN_YEAR' in yearObj;
    if (!years.length && hasUnknown) {
      ys.innerHTML = '<option value="UNKNOWN_YEAR">All years</option>';
      ys.disabled  = false; selYear = 'UNKNOWN_YEAR'; updateViewBtn();
    } else if (!years.length) {
      ys.innerHTML = '<option value="">No year data</option>'; ys.disabled = true;
    } else {
      const allOpt = hasUnknown ? '<option value="ALL">All years</option>' : '';
      ys.innerHTML = '<option value="">— Select year —</option>' + allOpt +
        years.map(y => `<option value="${y}">${y}</option>`).join('');
      ys.disabled = false;
    }
  }

  function onYearChange() { selYear = $('dac-year-select').value; updateViewBtn(); }

  function updateViewBtn() {
    $('dac-view-btn').disabled = !(selModel && selSubModel && selYear);
    if (selModel && selSubModel && selYear) {
      const yr = (selYear === 'UNKNOWN_YEAR' || selYear === 'ALL') ? '' : ' ' + selYear;
      $('dac-sel-summary').textContent = selFamily.name + ' · ' + selSubModel + yr;
    }
  }

  // ── Accessory grid ────────────────────────────────────────────────────────

  function loadAccessories() {
    if (!indexData || !selModel || !selSubModel || !selYear) return;
    const modelData = indexData.superModels[selModel][selSubModel] || {};
    let items = [];
    if (selYear === 'ALL') {
      const seen = new Set();
      Object.values(modelData).forEach(arr =>
        arr.forEach(i => { if (!seen.has(i.code)) { seen.add(i.code); items.push(i); } })
      );
    } else if (selYear === 'UNKNOWN_YEAR') {
      items = modelData['UNKNOWN_YEAR'] || [];
    } else {
      const seen = new Set();
      [...(modelData['UNKNOWN_YEAR'] || []), ...(modelData[selYear] || [])].forEach(i => {
        if (!seen.has(i.code)) { seen.add(i.code); items.push(i); }
      });
    }
    activeItems = items; activeCat = 'All';
    $('dac-sn2').classList.add('done');
    const yr = (selYear === 'UNKNOWN_YEAR' || selYear === 'ALL') ? '' : ' ' + selYear;
    $('dac-results-title').textContent = selFamily.name + ' · ' + selSubModel + yr;
    $('dac-results').classList.remove('dac-hidden');
    $('dac-search').value = ''; $('dac-lc-filter').value = 'all';
    buildCatChips(activeItems); renderGrid();
    $('dac-results').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function getCategory(name) {
    const n = name.toLowerCase();
    for (const [cat, kws] of Object.entries(CAT_KW)) if (kws.some(k => n.includes(k))) return cat;
    return 'Other';
  }

  function buildCatChips(items) {
    const cats = ['All', ...new Set(items.map(i => getCategory(i.name)))];
    $('dac-chips').innerHTML = cats.map(c =>
      `<span class="dac-chip${c === 'All' ? ' on' : ''}" data-cat="${escHtml(c)}">${escHtml(c)}</span>`
    ).join('');
  }

  function renderGrid() {
    const q = $('dac-search').value.toLowerCase();
    const lc = $('dac-lc-filter').value;
    const seen = new Set();
    const filtered = activeItems.filter(item => {
      if (seen.has(item.code)) return false; seen.add(item.code);
      return (!q || item.name.toLowerCase().includes(q) || item.code.toLowerCase().includes(q)) &&
        (lc === 'all' || item.lastChance === lc) &&
        (activeCat === 'All' || getCategory(item.name) === activeCat);
    });
    $('dac-results-meta').textContent = filtered.length + ' accessories';
    const grid = $('dac-acc-grid'), empty = $('dac-empty');
    if (!filtered.length) { grid.innerHTML = ''; empty.classList.remove('dac-hidden'); return; }
    empty.classList.add('dac-hidden');
    grid.innerHTML = filtered.map(item => {
      const imgSrc = item.image ? item.image.replace(/\.jpg$/, '.th345.jpg') : '';
      return `
        <div class="dac-acc-card" data-code="${item.code}" data-family="${selFamily.id}">
          ${imgSrc
            ? `<img class="dac-acc-img" src="${imgSrc}" alt="${escHtml(item.name)}" loading="lazy" onerror="this.src='${item.image}'">`
            : `<div class="dac-acc-img" style="display:flex;align-items:center;justify-content:center;color:#ccc;font-size:11px">No image</div>`}
          <div class="dac-acc-body">
            <div class="dac-acc-name">${escHtml(item.name)}</div>
            <div class="dac-acc-code">${item.code}</div>
            ${item.lastChance === '1' ? '<span class="dac-badge-lc">Last chance</span>' : ''}
          </div>
        </div>`;
    }).join('');
  }

  // ── Detail modal ──────────────────────────────────────────────────────────

  async function openDetail(code, familyId) {
    $('dac-modal-content').innerHTML =
      `<div class="dac-loading"><span class="dac-spinner"></span>Loading&hellip;</div>`;
    $('dac-overlay').classList.add('open');

    const key = `${code}_${familyId}`;
    if (!detailCache[key]) {
      try {
        const res = await fetch(`${S3}/accessories/details/${code}/${familyId}.json`);
        if (!res.ok) throw new Error(res.status);
        detailCache[key] = await res.json();
      } catch (e) {
        $('dac-modal-content').innerHTML = `
          <div class="dac-modal-body">
            <p style="color:#888;font-size:14px;padding:1rem 0">Could not load detail for <code>${code}</code>.</p>
          </div>`;
        return;
      }
    }
    await renderModal(detailCache[key]);
  }

  async function renderModal(d) {
    const imgs      = d.imageAttributes || [];
    const mainImgA  = imgs.find(i => i.attributeCode === 'MAIN_IMAGE') || imgs[0];
    const mainImg   = mainImgA ? mainImgA.accImage : null;
    const otherImgs = imgs.filter(i => i !== mainImgA && i.accImage);
    const variants  = d.accessoryVariants || [];
    const attach    = (d.accessoryAttributes || []).find(a => a.attributeCode === 'TECH_SPEC_ATTACH');
    const pdf       = attach && attach.attributeValueList && attach.attributeValueList[0];

    // Fetch Shopify data for all SKUs in parallel
    const shopifyData = {};
    await Promise.all(variants.map(async v => {
      if (v.sku) shopifyData[v.sku] = await lookupSku(v.sku);
    }));

    // Images
    let imgsHtml = '';
    if (mainImg) {
      const mainSrc = mainImg.image448Path || mainImg.imagePath || mainImg.thumbPath;
      imgsHtml = `
        <div class="dac-modal-imgs">
          <img class="dac-modal-img-main" id="dac-main-img" src="${mainSrc}" alt="${escHtml(d.name)}">
          <div class="dac-modal-thumbs">
            <img class="dac-modal-thumb active" src="${mainImg.thumbPath}" data-src="${mainSrc}" alt="main">
            ${otherImgs.map(i => {
              const src = i.accImage.image448Path || i.accImage.imagePath;
              return `<img class="dac-modal-thumb" src="${i.accImage.thumbPath}" data-src="${src}" alt="view">`;
            }).join('')}
          </div>
        </div>`;
    }

    // SKU rows with Shopify data
    let skuHtml = '';
    if (variants.length) {
      const rows = variants.map(v => {
        const status  = (v.partDetails && v.partDetails.partStatus) || '';
        const shop    = shopifyData[v.sku] || { found: false };
        const fits    = [...new Set(
          (v.applicabilitiesText || []).map(a =>
            a.myRangeLabel ? `${a.model} (${a.myRangeLabel})` :
            a.modelYear    ? `${a.model} ${a.modelYear}` : a.model
          )
        )];

        // Shopify price + cart cell
        let shopCell = '';
        if (shop.found) {
          const price      = formatPrice(shop.price);
          const compareAt  = shop.compareAt ? formatPrice(shop.compareAt) : null;
          const priceHtml  = compareAt
            ? `<span class="dac-price">${price}</span> <span class="dac-compare-at">${compareAt}</span>`
            : `<span class="dac-price">${price}</span>`;

          if (shop.available) {
            shopCell = `
              <div class="dac-shop-cell">
                <div class="dac-price-row">${priceHtml}</div>
                <button class="dac-atc-btn" data-variant="${shop.variantId}">Add to cart</button>
              </div>`;
          } else {
            shopCell = `
              <div class="dac-shop-cell">
                <div class="dac-price-row">${priceHtml}</div>
                <a class="dac-view-btn-sm" href="${shop.productUrl}">View product</a>
                <span class="dac-oos">Out of stock</span>
              </div>`;
          }
        } else {
          shopCell = `<div class="dac-shop-cell dac-not-listed">
            <a href="mailto:parts@ducatiredmond.com?subject=Parts inquiry: ${v.sku}"
               class="dac-contact-link">Request quote</a>
          </div>`;
        }

        return `<tr>
          <td>
            <span class="dac-sku-mono">${escHtml(v.sku || '—')}</span>
            ${v.isKit ? '<span class="dac-badge-kit">Kit</span>' : ''}
          </td>
          <td><span class="dac-sku-descr">${escHtml(v.descr || '—')}</span></td>
          <td><span class="dac-status-pill dac-status-${status}">${STATUS_LABEL[status] || status || '—'}</span></td>
          <td>
            <div class="dac-compat-chips">
              ${fits.map(f => `<span class="dac-compat-chip">${escHtml(f)}</span>`).join('')}
            </div>
          </td>
          <td>${shopCell}</td>
        </tr>`;
      }).join('');

      skuHtml = `
        <div class="dac-section-label">SKUs &amp; availability</div>
        <div style="overflow-x:auto">
          <table class="dac-sku-table">
            <thead>
              <tr><th>SKU</th><th>Description</th><th>Status</th><th>Fits</th><th>Purchase</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    } else {
      skuHtml = `<p style="font-size:13px;color:#aaa;margin-top:1rem">No SKU data available for this family.</p>`;
    }

    $('dac-modal-content').innerHTML = `
      ${imgsHtml}
      <div class="dac-modal-body">
        <div class="dac-modal-name">${escHtml(d.name)}</div>
        <div class="dac-modal-code">${d.code}</div>
        ${d.lastChance === '1' ? '<span class="dac-badge-lc" style="margin-bottom:8px;display:inline-block">Last chance</span>' : ''}
        ${d.description ? `<div class="dac-modal-desc">${escHtml(d.description)}</div>` : ''}
        ${pdf ? `<div style="margin-bottom:1rem">&#128196;&nbsp;<a class="dac-pdf-link" href="${pdf.attributeValueImageUri}" target="_blank" rel="noopener">${escHtml(pdf.attributeValueDesc)}</a></div>` : ''}
        ${skuHtml}
      </div>`;
  }

  // ── Events ────────────────────────────────────────────────────────────────

  function bindEvents() {
    root.addEventListener('click', e => {
      const fb = e.target.closest('.dac-family-btn');
      if (fb) { selectFamily(fb.dataset.id, fb.dataset.name, fb.dataset.index); return; }

      if (e.target.id === 'dac-view-btn')  { loadAccessories(); return; }
      if (e.target.id === 'dac-reset-btn') { resetAll(); return; }

      if (e.target.classList.contains('dac-chip')) {
        activeCat = e.target.dataset.cat;
        document.querySelectorAll('.dac-chip').forEach(c => c.classList.remove('on'));
        e.target.classList.add('on');
        renderGrid(); return;
      }

      // Add to cart button inside modal
      if (e.target.classList.contains('dac-atc-btn')) {
        addToCart(e.target.dataset.variant, e.target); return;
      }

      const card = e.target.closest('.dac-acc-card');
      if (card) { openDetail(card.dataset.code, card.dataset.family); return; }

      if (e.target.id === 'dac-overlay' || e.target.id === 'dac-modal-close') {
        $('dac-overlay').classList.remove('open'); return;
      }

      if (e.target.classList.contains('dac-modal-thumb')) {
        $('dac-main-img').src = e.target.dataset.src;
        document.querySelectorAll('.dac-modal-thumb').forEach(t => t.classList.remove('active'));
        e.target.classList.add('active');
      }
    });

    root.addEventListener('change', e => {
      if (e.target.id === 'dac-model-select')    onModelChange();
      if (e.target.id === 'dac-submodel-select') onSubModelChange();
      if (e.target.id === 'dac-year-select')     onYearChange();
      if (e.target.id === 'dac-lc-filter')       renderGrid();
    });

    root.addEventListener('input', e => {
      if (e.target.id === 'dac-search') renderGrid();
    });
  }

  function resetAll() {
    selFamily = null; selModel = ''; selSubModel = ''; selYear = '';
    activeItems = []; activeCat = 'All'; indexData = null;
    document.querySelectorAll('.dac-family-btn').forEach(b => b.classList.remove('selected'));
    $('dac-sn1').classList.remove('done');
    $('dac-sn2').classList.remove('done');
    $('dac-step2').classList.add('dac-hidden');
    $('dac-results').classList.add('dac-hidden');
  }

  // ── Go ────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();